import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import Customers from '@/pages/Customers';
import Items from '@/pages/Items';
import Quotations from '@/pages/Quotations';
import Sales from '@/pages/Sales';
import Settings from '@/pages/Settings';

// Placeholder components for routes
const CustomersPage = () => <Customers />;
const ItemsPage = () => <Items />;
const QuotationsPage = () => <Quotations />;
const SalesPage = () => <Sales />;
const AppointmentsPage = () => <div className="p-6"><h1 className="text-2xl font-bold">Citas - Por implementar</h1></div>;
const ReportsPage = () => <div className="p-6"><h1 className="text-2xl font-bold">Reportes - Por implementar</h1></div>;
const SettingsPage = () => <Settings />;

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode; permission?: string | string[] }> = ({ 
  children, 
  permission 
}) => {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="card-body text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600">No tienes permisos para acceder a esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/customers" element={
                <ProtectedRoute permission={['view_customers', 'manage_customers']}>
                  <CustomersPage />
                </ProtectedRoute>
              } />
              <Route path="/items" element={
                <ProtectedRoute permission={['view_items', 'manage_items']}>
                  <ItemsPage />
                </ProtectedRoute>
              } />
              <Route path="/quotations" element={
                <ProtectedRoute permission={['view_quotations', 'manage_quotations']}>
                  <QuotationsPage />
                </ProtectedRoute>
              } />
              <Route path="/sales" element={
                <ProtectedRoute permission={['view_sales', 'manage_sales']}>
                  <SalesPage />
                </ProtectedRoute>
              } />
              <Route path="/appointments" element={
                <ProtectedRoute permission="manage_appointments">
                  <AppointmentsPage />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute permission={['view_reports', 'view_financial_reports']}>
                  <ReportsPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute permission="manage_companies">
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="*" element={
                <div className="p-6">
                  <div className="card">
                    <div className="card-body text-center">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Página No Encontrada</h2>
                      <p className="text-gray-600">La página que buscas no existe.</p>
                    </div>
                  </div>
                </div>
              } />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default App;